import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  useTheme,
  TableBody,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  TableSortLabel,
  TablePagination,
  useMediaQuery,
  Collapse,
  IconButton,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import TableViewIcon from "@mui/icons-material/TableView";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import * as XLSX from "xlsx";
//for pdf Export
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
const getWeekNumber = (date = new Date()) => {
  const d = new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ));

  // ISO week: Monday = 1, Sunday = 7
  const day = d.getUTCDay() || 7;

  // Move date to nearest Thursday
  d.setUTCDate(d.getUTCDate() + 4 - day);

  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNumber = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);

  return weekNumber;
};

// const getFirstLastDayOfWeek = (week, year = new Date().getFullYear()) => {
//   const firstDayOfYear = new Date(year, 0, 1);
//   const firstWeekDay = new Date(
//     firstDayOfYear.setDate(firstDayOfYear.getDate() + (week - 1) * 7)
//   );
//   const lastWeekDay = new Date(firstWeekDay);
//   lastWeekDay.setDate(firstWeekDay.getDate() + 6);
//   return (
//     firstWeekDay.toLocaleDateString() + " - " + lastWeekDay.toLocaleDateString()
//   );
// };
const getFirstLastDayOfWeek = (week, year = new Date().getFullYear()) => {
  // ISO week starts with Monday
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));

  const dayOfWeek = simple.getUTCDay();
  const isoWeekStart =
    dayOfWeek <= 4
      ? new Date(simple.setUTCDate(simple.getUTCDate() - simple.getUTCDay() + 1))
      : new Date(simple.setUTCDate(simple.getUTCDate() + 8 - simple.getUTCDay()));

  const isoWeekEnd = new Date(isoWeekStart);
  isoWeekEnd.setUTCDate(isoWeekStart.getUTCDate() + 6);

  return (
    isoWeekStart.toLocaleDateString() +
    ' - ' +
    isoWeekEnd.toLocaleDateString()
  );
};

const WeeklyReports = ({ units, loadingUnitSearchData }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(getWeekNumber() - 1);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportData, setReportData] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Unit Name");
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const unitMap =
    units?.reduce((acc, u) => {
      acc[u.id] = u.name;
      return acc;
    }, {}) || {};

  const filteredReportData = reportData
    ? Object.entries(reportData).filter(([unitId]) =>
      (unitMap[unitId] || unitId)
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    )
    : [];

  // Build last 7 weeks options
  useEffect(() => {
    const currentWeek = getWeekNumber();
    const options = [];
    for (let i = 1; i <= 7; i++) {
      const weekNum = currentWeek - i;
      if (weekNum > 0) {
        options.push({
          value: weekNum,
          label: `${getFirstLastDayOfWeek(currentWeek - i)} - Week ${weekNum}`,
        });
      }
    }
    setWeekOptions(options);
  }, []);

  const fetchWeeklyReport = async (week) => {
    if (!units || units.length === 0) return;

    try {
      setLoadingReport(true);
      setError(null);

      const payload = {
        week,
        year: new Date().getFullYear(),
        units: units.map((u) => u.id),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/precision-report-week`,
        payload,
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );

      setReportData(response.data.data.mydict);
    } catch (err) {
      console.error("Failed to fetch report data:", err);
      setError("Error fetching report data");
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (units && units.length > 0) {
      fetchWeeklyReport(selectedWeek);
    }
  }, [selectedWeek, units]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const copyToClipboard = () => {
    if (!sortedData || sortedData.length === 0) {
      console.warn("No report data to copy");
      return;
    }
    const textContent = sortedData
      .map(([unitId, report]) => {
        return `${unitMap[unitId] || unitId}\t${report.year}\t${report.week}\t${report.down
          }\t${report.up}\t${report.customer}\t${report.precision}\t${report.shutDowns
          }`;
      })
      .join("\n");

    const headers =
      "Unit Name\tYear\tWeek\tStop(Hrs)\tRun(Hrs)\tCustomer(Hrs)\tPRC(Hrs)\tShutdowns";

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => toast.success(`Weekly report copied to clipboard`))
      .catch((err) => console.error("Failed to copy:", err));

    handleExportMenuClose();
  };

  const exportToCSV = () => {
    if (!sortedData || sortedData.length === 0) {
      console.warn("No report data to export");
      return;
    }
    const csvContent = [
      [
        "Unit Name",
        "Year",
        "Week",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
      ...sortedData.map(([unitId, report]) => [
        unitMap[unitId] || unitId,
        report.year,
        report.week,
        report.down,
        report.up,
        report.customer,
        report.precision,
        report.shutDowns,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Weekly_Report.csv`;
    link.click();

    handleExportMenuClose();
    toast.success(`Exported Weekly Report to CSV`);
  };

  const exportToExcel = () => {
    if (!sortedData || sortedData.length === 0) {
      console.warn("No report data to export");
      return;
    }

    const headers = [
      "Unit Name",
      "Year",
      "Week",
      "Stop (Hrs)",
      "Run (Hrs)",
      "Customer (Hrs)",
      "PRC (Hrs)",
      "Shutdowns",
    ];
    const rows = sortedData.map(([unitId, report]) => [
      unitMap[unitId] || unitId,
      report.year,
      report.week,
      report.down,
      report.up,
      report.customer,
      report.precision,
      report.shutDowns,
    ]);

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Weekly Report");

    // Export file
    XLSX.writeFile(workbook, `Weekly_Report.xlsx`);

    handleExportMenuClose();
    toast.success(`Exported Weekly Report to Excel`);
  };

  const exportToPDF = () => {
    if (!sortedData || sortedData.length === 0) {
      console.warn("No report data to export");
      return;
    }

    const doc = new jsPDF("l", "pt", "a4"); // landscape, points, A4

    // Title
    doc.setFontSize(12);
    doc.text("Weekly Units Report", 40, 30);

    // Table headers
    const headers = [
      [
        "Unit Name",
        "Year",
        "Week",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
    ];

    // Table rows from sortedData
    const rows = sortedData.map(([unitId, report]) => [
      unitMap[unitId] || unitId, // Unit Name
      report.year,
      report.week,
      report.down,
      report.up,
      report.customer,
      report.precision,
      report.shutDowns,
    ]);

    // Render table with autoTable
    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: { fillColor: [41, 128, 185] },
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 90 }, // Unit Name
        1: { cellWidth: 50 }, // Year
        2: { cellWidth: 60 }, // Week
        3: { cellWidth: 70 }, // Stop (Hrs)
        4: { cellWidth: 70 }, // Run (Hrs)
        5: { cellWidth: 90 }, // Customer (Hrs)
        6: { cellWidth: 70 }, // PRC (Hrs)
        7: { cellWidth: 70 }, // Shutdowns
      },
    });

    // Save file
    doc.save(`Weekly_Report.pdf`);
    handleExportMenuClose();
    toast.success(`Exported Weekly Report to PDF`);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = React.useMemo(() => {
    if (!filteredReportData) return [];

    return [...filteredReportData].sort(([aId, aReport], [bId, bReport]) => {
      let aValue, bValue;

      switch (orderBy) {
        case "Unit Name":
          aValue = unitMap[aId] || aId;
          bValue = unitMap[bId] || bId;
          break;
        case "Year":
          aValue = aReport.year;
          bValue = bReport.year;
          break;
        case "Week":
          aValue = aReport.week;
          bValue = bReport.week;
          break;
        case "Stop(Hrs)":
          aValue = aReport.down;
          bValue = bReport.down;
          break;
        case "Run(Hrs)":
          aValue = aReport.up;
          bValue = bReport.up;
          break;
        case "Customer(Hrs)":
          aValue = aReport.customer;
          bValue = bReport.customer;
          break;
        case "PRC(Hrs)":
          aValue = aReport.precision;
          bValue = bReport.precision;
          break;
        case "Shutdowns":
          aValue = aReport.shutDowns;
          bValue = bReport.shutDowns;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredReportData, order, orderBy]);

  const paginatedUnits = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const columns = [
    { label: "Unit Name", hideOnMobile: false },
    { label: "Year", hideOnMobile: false },
    { label: "Week", hideOnMobile: true },
    { label: "Stop(Hrs)", hideOnMobile: true },
    { label: "Run(Hrs)", hideOnMobile: true },
    { label: "Customer(Hrs)", hideOnMobile: true },
    { label: "PRC(Hrs)", hideOnMobile: true },
    { label: "Shutdowns", hideOnMobile: true },
  ];

  const renderTable = () => (
    <TableContainer
      sx={{
        pl: 2,
        flexGrow: 1,
        height: "63vh",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            <TableCell
              sx={{
                display: { xs: "table-cell", md: "none" },
                fontWeight: "bold",
                textAlign: "center",
                fontSize: "12px",
                padding: "12px 8px",
                width: "50px",
              }}
            >
              Details
            </TableCell>
            {columns.map(({ label, hideOnMobile }) => (
              <TableCell
                key={label}
                sx={{
                  display: hideOnMobile
                    ? { xs: "none", md: "table-cell" }
                    : "table-cell",
                }}
              >
                <TableSortLabel
                  active={orderBy === label}
                  direction={orderBy === label ? order : "asc"}
                  onClick={() => handleRequestSort(label)}
                >
                  {label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedUnits.length > 0 ? (
            paginatedUnits.map(([unitId, report], index) => (
              <React.Fragment key={unitId}>
                <TableRow
                  key={unitId}
                  hover
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    "&:hover": {
                      backgroundColor: "#4a4d54",
                    },
                  }}
                >
                  {/* Expand Button - Mobile Only */}
                  <TableCell
                    sx={{
                      display: { xs: "table-cell", md: "none" },
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(unitId)}
                      sx={{ color: "white" }}
                    >
                      {expandedRows[unitId] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{unitMap[unitId] || unitId}</TableCell>
                  <TableCell>{report.year}</TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {report.week}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {report.down}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {report.up}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {report.customer}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {report.precision}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {report.shutDowns}
                  </TableCell>
                </TableRow>
                {/* Mobile Collapsed Row */}
                <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={3}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    }}
                  >
                    <Collapse
                      in={expandedRows[unitId]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ py: 2, px: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Month:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {new Date(
                                report.year,
                                report.month - 1
                              ).toLocaleString("default", {
                                month: "long",
                              })}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Stop(Hrs):
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {report.down}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Run(Hrs):
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {report.up}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Customer(Hrs):
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {report.customer}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              PRC(Hrs):
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {report.precision}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Shutdowns:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {report.shutDowns}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                No units found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        width: "100%", // Full width
        display: "flex",
        justifyContent: "center", // Center the inner container
        flexDirection: "column",
      }}
    >
      <Box>
        {loadingUnitSearchData && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(187, 183, 183, 0.12)",
              backdropFilter: "blur(10px)",
              zIndex: 10,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color="#60a5fa" size={70} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between", // Space between title and button
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mt: 1,
          mb: 2,
          px: 2, // Optional horizontal padding
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            fontWeight="bold"
            sx={{ fontSize: isSmallScreen ? 14 : 18 }}
          >
            Weekly Runtime Report
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Button
            sx={{ borderRadius: 0 }}
            variant="outlined"
            size={isSmallScreen ? "small" : "large"}
            color="white"
            startIcon={<GetAppIcon />}
            onClick={handleExportMenuOpen}
          >
            Export
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: `${isSmallScreen ? "column" : "row"}`,
          justifyContent: "space-between",
          alignItems: `${isSmallScreen ? "start" : "center"}`,
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
          px: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            label="Search Unit..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 150, width: 200 }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControl sx={{ minWidth: 200, width: 300 }} size="small">
            <InputLabel id="week-select-label">Select Week</InputLabel>
            {weekOptions.length > 0 && (
              <Select
                labelId="week-select-label"
                value={selectedWeek}
                label="Select Week"
                onChange={(e) => setSelectedWeek(e.target.value)}
              >
                {weekOptions.map((w) => (
                  <MenuItem key={w.value} value={w.value}>
                    {w.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
        </Box>
      </Box>
      {loadingReport ? (
        <Box
          sx={{
            width: "100%",
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            zIndex: 10,
          }}
        >
          <CircularProgress color="#60a5fa" size={60} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            color: "red",
            fontWeight: "bold",
            py: 2,
          }}
        >
          {error}
        </Box>
      ) : (
        <>
          {renderTable()}
          <TablePagination
            component="div"
            count={filteredReportData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            sx={{ mt: 1 }}
          />
        </>
      )}

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
      >
        <MenuItem onClick={copyToClipboard}>
          <ListItemIcon>
            <FileCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToCSV}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToExcel}>
          <ListItemIcon>
            <TableViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToPDF}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>PDF</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default WeeklyReports;
